import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, inject, OnInit, DestroyRef, signal, effect, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { PdfViewerComponent, PdfViewerModule } from 'ng2-pdf-viewer';

import { ApiService } from '../../services/api.service';
import { PdfAnnotationStateService } from '../../services/pdf-annotation-state.service';
import { ApiResponse, PdfAnnotation, SmartPdfUploadResult } from '../../models';

type PdfLoadProgress = {
  loaded: number;
  total: number;
};

type PdfDocumentLike = {
  numPages: number;
  getOutline?: () => Promise<PdfOutlineItemLike[] | null>;
  getDestination?: (dest: string) => Promise<unknown[] | null>;
  getPageIndex?: (ref: { num: number; gen: number }) => Promise<number>;
};

type PdfOutlineItemLike = {
  title?: string;
  dest?: string | unknown[] | null;
  items?: PdfOutlineItemLike[];
};

type BookmarkTarget = {
  pageNumber: number;
  destArray: unknown[] | null;
};

type MarkerRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

@Component({
  selector: 'app-pdf-schematic-viewer',
  standalone: true,
  imports: [CommonModule, PdfViewerModule],
  templateUrl: './pdf-schematic-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './pdf-schematic-viewer.component.css'
})
export class PdfSchematicViewerComponent implements OnChanges, OnInit {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly pdfAnnotationStateService = inject(PdfAnnotationStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly debugLoggingEnabled = true;

  @Input() pdfUrl = '';
  @Input() searchQuery = '';
  @Input() showPopoutButton = false;
  @Input() fullBleedMode = false;
  @Input() showControls = true;
  @Input() enableDoubleClickPopout = false;
  @Input() maxFileSizeBytes = 50 * 1024 * 1024;

  @Output() popoutRequested = new EventEmitter<void>();
  @Output() uploadStarted = new EventEmitter<string>();
  @Output() uploadSucceeded = new EventEmitter<SmartPdfUploadResult>();
  @Output() uploadFailed = new EventEmitter<string>();

  @ViewChild(PdfViewerComponent) private pdfComponent?: PdfViewerComponent;
  pdfSourceData: Uint8Array | null = null;

  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  readonly currentPage = signal(1);
  zoom = 1;
  progressPct = 0;
  totalPages = 0;
  renderedPages = 0;
  lastError = '';
  searchMatches = 0;
  dragActive = false;
  uploading = false;
  selectedFileName = '';
  private searchSyncTimer: number | null = null;
  private centerHighlightTimer: number | null = null;
  private bookmarkTargets = new Map<string, BookmarkTarget>();
  private pdfDocument: PdfDocumentLike | null = null;
  private bookmarkMarkerTimer: number | null = null;
  private bookmarkMarkerElement: HTMLElement | null = null;
  private bookmarkCenterElement: HTMLElement | null = null;
  private bookmarkAnnotationElement: HTMLElement | null = null;
  private activeBookmarkMarker: BookmarkTarget | null = null;
  private activeBookmarkQuery: string | null = null;
  private readonly annotations = signal<PdfAnnotation[]>([]);

  readonly hoveredAnnotationXref = signal<number | null>(null);
  readonly selectedAnnotationXref = signal<number | null>(null);

  constructor() {
    effect(() => {
      // This effect will run whenever selected or hovered xref changes.
      const selected = this.selectedAnnotationXref();
      const hovered = this.hoveredAnnotationXref();
      this.debugLog('style-update-effect', { selected, hovered });

      // In practice, ng2-pdf-viewer with show-all=false only has one page's elements
      // in the DOM. So just updating the current page is sufficient and correct.
      this.updateAnnotationStylesForPage(this.currentPage());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfUrl']) {
      this.resetViewerState();
      if (this.pdfUrl) {
        this.loadingState = 'loading';
        this.debugLog('loading', { pdfUrl: this.pdfUrl });
        // Fetch the file securely through Angular instead of letting pdf.js do it
        this.loadPdfSecurely(this.pdfUrl);
      }
    }
  }

  //  Secure binary transport stream pipeline
  private loadPdfSecurely(url: string): void {
    this.http.get(url, { responseType: 'arraybuffer' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (buffer) => {
          this.pdfSourceData = new Uint8Array(buffer);
          // Note: Leave this.loadingState = 'loading' here; 
          // onPdfLoaded() will cleanly flip it to 'loaded' once the viewer parses the bytes!
        },
        error: (err) => {
          this.onPdfError(err);
        }
      });
  }

  ngOnInit(): void {
    this.pdfAnnotationStateService.annotations$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(annotations => {
        this.annotations.set(annotations);
        if (annotations.length > 0) {
          this.debugLog('annotations-loaded', { count: annotations.length });
          // Render annotations for the current page now that we have data
          this.renderAnnotationsForPage(this.currentPage());
        }
      });

    this.pdfAnnotationStateService.componentToHighlight$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(designator => {
        this.debugLog('highlight-request-received', { designator });
        this.selectAndHighlightComponent(designator);
      });
  }

  onLoadProgress(progress: PdfLoadProgress): void {
    if (!progress.total) {
      return;
    }

    this.progressPct = Math.round((progress.loaded / progress.total) * 100);
  }

  onPdfLoaded(pdf: PdfDocumentLike): void {
    this.loadingState = 'loaded';
    this.currentPage.set(1);
    this.totalPages = pdf.numPages;
    this.lastError = '';
    this.pdfDocument = pdf;
    this.debugLog('loaded', { pages: pdf.numPages, pdfUrl: this.pdfUrl });
    void this.buildBookmarkIndex(pdf);
  }

  onPageRendered(): void {
    this.renderedPages += 1;
    this.debugLog('page-rendered', {
      currentPage: this.currentPage(),
      renderedPages: this.renderedPages,
      totalPages: this.totalPages,
      zoom: this.zoom
    });
    this.renderAnnotationsForPage(this.currentPage());

  }

  onTextLayerRendered(): void {
    this.debugLog('text-layer-rendered', {
      currentPage: this.currentPage(),
      selectedHighlightExists: !!this.getSelectedHighlightElement()
    });
    this.syncSearchSelection();
  }

  zoomIn(): void {
    this.zoom = Math.min(this.zoom + 0.2, 6);

    this.renderAnnotationsForPage(this.currentPage());
  }

  zoomOut(): void {
    this.zoom = Math.max(this.zoom - 0.2, 0.4);

    this.renderAnnotationsForPage(this.currentPage());
  }

  resetZoom(): void {
    this.zoom = 1;

    this.renderAnnotationsForPage(this.currentPage());
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update((p) => p + 1);
      // onPageRendered will handle rendering annotations
    }
  }

  previousPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
      // onPageRendered will handle rendering annotations
    }
  }

  onPdfError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    this.loadingState = 'error';
    this.lastError = message;
    this.debugLog('render-error', error);
  }

  requestPopout(): void {
    this.popoutRequested.emit();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.uploading) {
      this.dragActive = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;

    if (this.uploading) {
      return;
    }

    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }

    this.validateAndUpload(file);
  }

  onViewerDoubleClick(): void {
    if (!this.enableDoubleClickPopout || !this.pdfUrl.trim()) {
      return;
    }

    this.requestPopout();
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onAnnotationClick(annotation: PdfAnnotation): void {
    this.debugLog('annotation-click', { designator: annotation.componentDesignator, page: annotation.page });
    this.pdfAnnotationStateService.selectComponentFromPdf(annotation.componentDesignator);
  }

  onAnnotationMouseEnter(annotation: PdfAnnotation): void {
    this.hoveredAnnotationXref.set(annotation.xref);
  }

  onAnnotationMouseLeave(): void {
    this.hoveredAnnotationXref.set(null);
  }

  private validateAndUpload(file: File): void {
    if (file.size <= 0) {
      this.uploadFailed.emit('Selected PDF file is empty.');
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      this.uploadFailed.emit('Only .pdf files are supported.');
      return;
    }

    if (file.size > this.maxFileSizeBytes) {
      this.uploadFailed.emit(
        `File is too large (${this.formatBytes(file.size)}). Max allowed is ${this.formatBytes(this.maxFileSizeBytes)}.`
      );
      return;
    }

    this.selectedFileName = file.name;
    this.uploading = true;
    this.uploadStarted.emit(file.name);

    this.apiService.uploadSmartPdf(file).subscribe({
      next: (response: ApiResponse<SmartPdfUploadResult>) => {
        this.uploading = false;
        this.uploadSucceeded.emit(response.data);
      },
      error: (err: unknown) => {
        this.uploading = false;
        this.uploadFailed.emit(err instanceof Error ? err.message : 'Failed to upload PDF schematic.');
      }
    });
  }

  private selectAndHighlightComponent(componentDesignator: string | null): void {
    if (!componentDesignator) {
      this.selectedAnnotationXref.set(null);
      return;
    }

    const annotation = this.annotations().find(a => this.normalizeAnnotationDesignator(a.componentDesignator) === this.normalizeAnnotationDesignator(componentDesignator));
    this.selectedAnnotationXref.set(annotation?.xref ?? null);

    if (annotation) {
      // This method handles navigation and centering
      this.highlightAnnotation(annotation);
    }
  }



  private highlightAnnotation(annotation: PdfAnnotation): void {
    // This method is now just for navigation and centering.
    // The selection is handled by setting `selectedAnnotationXref`.
    this.pdfComponent?.eventBus.dispatch('find', { query: '' });
    this.clearBookmarkMarker();

    if (annotation.page !== this.currentPage()) {
      this.currentPage.set(annotation.page);
      // The rects will be calculated in onPageRendered. We need to wait for that.
      setTimeout(() => {
        const el = this.hostElement.nativeElement.querySelector(`[data-xref="${annotation.xref}"]`);
        if (el instanceof HTMLElement) {
          this.centerElementInScrollContainer(el);
        }
      }, 200);
    } else {
      // Already on the right page.
      const el = this.hostElement.nativeElement.querySelector(`[data-xref="${annotation.xref}"]`);
      if (el instanceof HTMLElement) {
        this.centerElementInScrollContainer(el);
      }
    }
  }

  private renderAnnotationsForPage(pageNumber: number): void {
    const pdfComponent = this.pdfComponent;
    if (!pdfComponent?.pdfViewer) {
      return;
    }

    const pageView = (pdfComponent.pdfViewer as any).getPageView?.(pageNumber - 1);
    const pageElement = pageView?.div as HTMLElement | undefined;
    const viewport = pageView?.viewport;

    if (!pageElement || !viewport) {
      this.debugLog('render-annotations-skip', { reason: 'page-view-not-ready', page: pageNumber });
      // Retry once, as the page might still be initializing
      setTimeout(() => {
        const freshPageView = (pdfComponent.pdfViewer as any).getPageView?.(pageNumber - 1);
        if (freshPageView?.div) {
          this.renderAnnotationsForPage(pageNumber);
        }
      }, 200);
      return;
    }

    // Ensure a container for our annotations exists inside the page div
    let annotationLayer = pageElement.querySelector('.custom-annotation-layer') as HTMLElement;
    if (!annotationLayer) {
      annotationLayer = document.createElement('div');
      annotationLayer.className = 'custom-annotation-layer';
      pageElement.appendChild(annotationLayer);
    }

    // Clear previous annotations from this layer
    annotationLayer.innerHTML = '';

    const pageAnnotations = this.annotations().filter(a => a.page === pageNumber);

    for (const annotation of pageAnnotations) {
      const el = document.createElement('div');
      el.className = 'debug-rect';
      el.dataset['xref'] = String(annotation.xref);

      // The backend now sends pre-transformed, top-left based coordinates in PDF "point" units.
      // We just need to scale them by the viewport's current scale to get pixel values.
      const [x0, y0, x1, y1] = annotation.rect;
      const scale = viewport.scale;

      const left = x0 * scale;
      const top = y0 * scale;
      const width = (x1 - x0) * scale;
      const height = (y1 - y0) * scale;

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${width}px`;
      el.style.height = `${height}px`;

      el.addEventListener('click', () => this.onAnnotationClick(annotation));
      el.addEventListener('mouseenter', () => this.onAnnotationMouseEnter(annotation));
      el.addEventListener('mouseleave', () => this.onAnnotationMouseLeave());

      annotationLayer.appendChild(el);
    }

    this.debugLog('annotations-rendered', { page: pageNumber, count: pageAnnotations.length });
    this.updateAnnotationStylesForPage(pageNumber);
  }



  private updateAnnotationStylesForPage(pageNumber: number): void {
    const pdfComponent = this.pdfComponent;
    if (!pdfComponent?.pdfViewer) return;

    const pageView = (pdfComponent.pdfViewer as any).getPageView?.(pageNumber - 1);
    const pageElement = pageView?.div as HTMLElement | undefined;
    if (!pageElement) return;

    const annotationLayer = pageElement.querySelector('.custom-annotation-layer');
    if (!annotationLayer) return;

    const selected = this.selectedAnnotationXref();
    const hovered = this.hoveredAnnotationXref();

    for (const el of Array.from(annotationLayer.children)) {
      const xref = Number((el as HTMLElement).dataset['xref']);
      el.classList.toggle('selected', xref === selected);
      el.classList.toggle('hovered', xref === hovered);
    }
  }

  private async buildBookmarkIndex(pdf: PdfDocumentLike): Promise<void> {
    this.bookmarkTargets.clear();

    if (!pdf.getOutline || !pdf.getPageIndex) {
      this.debugLog('bookmark-index-skip', { reason: 'pdf-outline-api-unavailable' });
      return;
    }

    try {
      const outline = await pdf.getOutline();
      if (!outline || outline.length === 0) {
        this.debugLog('bookmark-index-empty', { reason: 'no-outline' });
        return;
      }

      await this.visitOutlineItems(outline, pdf);
      this.debugLog('bookmark-index-ready', { count: this.bookmarkTargets.size });
    } catch (error) {
      this.debugLog('bookmark-index-error', error);
    }
  }

  private async visitOutlineItems(items: PdfOutlineItemLike[], pdf: PdfDocumentLike): Promise<void> {
    for (const item of items) {
      const title = (item.title ?? '').trim();
      if (title) {
        const resolved = await this.resolveOutlineDestination(item.dest ?? null, pdf);
        if (resolved) {
          this.bookmarkTargets.set(this.normalizeBookmarkKey(title), resolved);
        }
      }

      if (item.items && item.items.length > 0) {
        await this.visitOutlineItems(item.items, pdf);
      }
    }
  }

  private async resolveOutlineDestination(
    dest: string | unknown[] | null,
    pdf: PdfDocumentLike
  ): Promise<BookmarkTarget | null> {
    let explicitDest: unknown[] | null = null;

    if (typeof dest === 'string') {
      explicitDest = pdf.getDestination ? await pdf.getDestination(dest) : null;
    } else if (Array.isArray(dest)) {
      explicitDest = dest;
    }

    if (!explicitDest || !pdf.getPageIndex) {
      return null;
    }

    const destRef = explicitDest[0] as unknown;
    let pageIndex: number | null = null;

    if (
      typeof destRef === 'object' &&
      destRef !== null &&
      'num' in (destRef as Record<string, unknown>) &&
      'gen' in (destRef as Record<string, unknown>)
    ) {
      pageIndex = await pdf.getPageIndex(destRef as { num: number; gen: number });
    } else if (typeof destRef === 'number') {
      pageIndex = destRef;
    }

    if (pageIndex === null || Number.isNaN(pageIndex)) {
      return null;
    }

    return {
      pageNumber: pageIndex + 1,
      destArray: explicitDest
    };
  }

  private normalizeBookmarkKey(value: string): string {
    return value
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9_-]/g, '');
  }

  private normalizeAnnotationDesignator(value: string): string {
    if (!value) {
      console.log('normalizeAnnotationDesignator got null')
      return '';
    }
    const cleaned = value
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');

    const reversedMatch = cleaned.match(/^(\d+)([A-Z]+)$/);
    if (reversedMatch) {
      return `${reversedMatch[2]}${reversedMatch[1]}`;
    }

    return cleaned;
  }

  private scheduleSearchSync(): void {
    if (this.searchSyncTimer !== null) {
      window.clearTimeout(this.searchSyncTimer);
    }

    this.searchSyncTimer = window.setTimeout(() => {
      this.searchSyncTimer = null;
      this.syncSearchSelection();
    }, 180);
  }

  private syncSearchSelection(): void {
    const pdfComponent = this.pdfComponent;
    const query = this.searchQuery.trim();
    if (!pdfComponent || !query || this.loadingState !== 'loaded') {
      return;
    }

    const findController = pdfComponent.pdfFindController as unknown as {
      _selected?: { pageIdx: number; matchIdx: number };
      _pageMatches?: Array<Array<number>>;
    };

    const selectedPageIdx = findController._selected?.pageIdx ?? -1;
    const selectedMatchIdx = findController._selected?.matchIdx ?? -1;
    const selectedPage = selectedPageIdx + 1;
    this.searchMatches = (findController._pageMatches ?? []).reduce((total, matches) => total + matches.length, 0);

    this.debugLog('search-sync', {
      query,
      currentPage: this.currentPage(),
      selectedPage,
      selectedMatchIdx,
      searchMatches: this.searchMatches,
      zoom: this.zoom,
      selectedHighlightExists: !!this.getSelectedHighlightElement()
    });

    if (selectedPage > 0 && this.currentPage() !== selectedPage) {
      this.currentPage.set(selectedPage);
      this.zoom = Math.max(this.zoom, 1.6);
      this.debugLog('search-page-jump', {
        selectedPage,
        zoom: this.zoom
      });
      this.scheduleSearchSync();
      return;
    }

    if (selectedPage > 0 && selectedMatchIdx >= 0) {
      this.scheduleCenterSelectedHighlight();
    }
  }

  private scheduleCenterSelectedHighlight(): void {
    if (this.centerHighlightTimer !== null) {
      window.clearTimeout(this.centerHighlightTimer);
    }

    this.centerHighlightTimer = window.setTimeout(() => {
      this.centerHighlightTimer = null;
      this.centerSelectedHighlight();
    }, 80);
  }

  private centerSelectedHighlight(): void {
    const selectedHighlight = this.getSelectedHighlightElement();
    const scrollContainer = this.getHighlightScrollContainer(selectedHighlight);

    if (!(scrollContainer instanceof HTMLElement) || !(selectedHighlight instanceof HTMLElement)) {
      this.debugLog('center-skip', {
        reason: 'missing-elements',
        hasScrollContainer: scrollContainer instanceof HTMLElement,
        hasSelectedHighlight: selectedHighlight instanceof HTMLElement
      });
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const highlightRect = selectedHighlight.getBoundingClientRect();
    const targetScrollLeft =
      scrollContainer.scrollLeft + (highlightRect.left - containerRect.left) - containerRect.width / 2 + highlightRect.width / 2;
    const targetScrollTop =
      scrollContainer.scrollTop + (highlightRect.top - containerRect.top) - containerRect.height / 2 + highlightRect.height / 2;

    this.debugLog('center-before', {
      containerClassName: scrollContainer.className,
      scrollLeft: scrollContainer.scrollLeft,
      scrollTop: scrollContainer.scrollTop,
      scrollWidth: scrollContainer.scrollWidth,
      scrollHeight: scrollContainer.scrollHeight,
      clientWidth: scrollContainer.clientWidth,
      clientHeight: scrollContainer.clientHeight,
      containerRect: containerRect.toJSON?.() ?? null,
      highlightRect: highlightRect.toJSON?.() ?? null,
      targetScrollLeft,
      targetScrollTop
    });

    scrollContainer.scrollTo({
      left: Math.max(0, targetScrollLeft),
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });

    window.requestAnimationFrame(() => {
      this.debugLog('center-after', {
        containerClassName: scrollContainer.className,
        scrollLeft: scrollContainer.scrollLeft,
        scrollTop: scrollContainer.scrollTop,
        scrollWidth: scrollContainer.scrollWidth,
        scrollHeight: scrollContainer.scrollHeight,
        clientWidth: scrollContainer.clientWidth,
        clientHeight: scrollContainer.clientHeight,
        containerRect: scrollContainer.getBoundingClientRect().toJSON?.() ?? null,
        highlightRect: selectedHighlight.getBoundingClientRect().toJSON?.() ?? null
      });
    });
  }

  private getHighlightScrollContainer(highlight: HTMLElement | null): HTMLElement | null {
    const pdfContainer = this.pdfComponent?.pdfViewerContainer?.nativeElement;
    if (pdfContainer instanceof HTMLElement) {
      return pdfContainer;
    }

    let current = highlight?.parentElement ?? null;
    while (current) {
      if (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth) {
        return current;
      }
      current = current.parentElement;
    }

    const viewerWrap = this.hostElement.nativeElement.querySelector('.pdf-viewer-wrap');
    return viewerWrap instanceof HTMLElement ? viewerWrap : null;
  }

  private getSelectedHighlightElement(): HTMLElement | null {
    const selectedHighlight = this.hostElement.nativeElement.querySelector('.textLayer .highlight.selected');
    return selectedHighlight instanceof HTMLElement ? selectedHighlight : null;
  }



  private findNativeAnnotationOnPage(
    pageElement: HTMLElement,
    query: string,
    destinationAnchor: { x: number; y: number } | null
  ): { element: HTMLElement; rect: MarkerRect } | null {
    const target = this.normalizeBookmarkKey(query);
    const pageRect = pageElement.getBoundingClientRect();

    const candidates = Array.from(pageElement.querySelectorAll('.annotationLayer .linkAnnotation'))
      .filter((el): el is HTMLElement => el instanceof HTMLElement)
      .map((el) => {
        const annotationId = el.getAttribute('data-annotation-id') ?? '';
        const domId = el.id ?? '';
        const idMatch = this.normalizeBookmarkKey(annotationId) === target;
        const domIdMatch = this.normalizeBookmarkKey(domId).includes(target);
        if (!idMatch && !domIdMatch) {
          return null;
        }

        const rect = el.getBoundingClientRect();
        const centerX = rect.left - pageRect.left + rect.width / 2;
        const centerY = rect.top - pageRect.top + rect.height / 2;
        const distance = destinationAnchor
          ? Math.hypot(centerX - destinationAnchor.x, centerY - destinationAnchor.y)
          : 0;
        return { el, rect, centerX, centerY, distance };
      })
      .filter((entry): entry is NonNullable<typeof entry> => !!entry)
      .sort((a, b) => a.distance - b.distance);

    const best = candidates[0];
    if (!best) {
      return null;
    }

    return {
      element: best.el,
      rect: {
        left: Math.max(0, best.rect.left - pageRect.left),
        top: Math.max(0, best.rect.top - pageRect.top),
        width: Math.max(1, best.rect.width),
        height: Math.max(1, best.rect.height)
      }
    };
  }

  private computeDestinationAnchor(
    destArray: unknown[] | null,
    viewport: any,
    pageElement: HTMLElement
  ): { x: number; y: number } | null {
    if (!Array.isArray(destArray) || destArray.length < 2) {
      return {
        x: pageElement.clientWidth / 2,
        y: pageElement.clientHeight / 2
      };
    }

    const mode = (destArray[1] as { name?: string } | null)?.name ?? 'XYZ';

    if (mode === 'FitR' && destArray.length >= 6) {
      const left = typeof destArray[2] === 'number' ? destArray[2] : null;
      const bottom = typeof destArray[3] === 'number' ? destArray[3] : null;
      const right = typeof destArray[4] === 'number' ? destArray[4] : null;
      const top = typeof destArray[5] === 'number' ? destArray[5] : null;

      if (left !== null && bottom !== null && right !== null && top !== null) {
        const [vx, vy] = viewport.convertToViewportPoint((left + right) / 2, (top + bottom) / 2);
        return { x: vx, y: vy };
      }
    }

    const x = typeof destArray[2] === 'number' ? destArray[2] : null;
    const y = typeof destArray[3] === 'number' ? destArray[3] : null;

    if (x === null || y === null) {
      return {
        x: pageElement.clientWidth / 2,
        y: pageElement.clientHeight / 2
      };
    }

    const [vx, vy] = viewport.convertToViewportPoint(x, y);
    return { x: vx, y: vy };
  }

  private computeMarkerRectFromAnchor(
    anchor: { x: number; y: number } | null,
    pageElement: HTMLElement
  ): MarkerRect | null {
    const markerWidth = 28;
    const markerHeight = 20;
    const x = anchor?.x ?? pageElement.clientWidth / 2;
    const y = anchor?.y ?? pageElement.clientHeight / 2;

    return {
      left: Math.max(0, x - markerWidth / 2),
      top: Math.max(0, y - markerHeight / 2),
      width: markerWidth,
      height: markerHeight
    };
  }

  private findLabelRectOnPage(
    pageElement: HTMLElement,
    query: string,
    destinationAnchor: { x: number; y: number } | null
  ): MarkerRect | null {
    const target = this.normalizeBookmarkKey(query);
    const pageRect = pageElement.getBoundingClientRect();

    const candidates = Array.from(pageElement.querySelectorAll('.textLayer span'))
      .filter((el): el is HTMLSpanElement => el instanceof HTMLSpanElement)
      .filter((el) => this.normalizeBookmarkKey(el.textContent ?? '') === target)
      .map((el) => {
        const rect = el.getBoundingClientRect();
        const centerX = rect.left - pageRect.left + rect.width / 2;
        const centerY = rect.top - pageRect.top + rect.height / 2;
        const distance = destinationAnchor
          ? Math.hypot(centerX - destinationAnchor.x, centerY - destinationAnchor.y)
          : 0;
        return { el, rect, centerX, centerY, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    const best = candidates[0];
    if (!best) {
      return null;
    }

    const paddingX = 8;
    const paddingY = 5;
    return {
      left: Math.max(0, best.rect.left - pageRect.left - paddingX),
      top: Math.max(0, best.rect.top - pageRect.top - paddingY),
      width: Math.max(22, best.rect.width + paddingX * 2),
      height: Math.max(14, best.rect.height + paddingY * 2)
    };
  }

  private centerElementInScrollContainer(element: HTMLElement): void {
    const scrollContainer = this.getHighlightScrollContainer(element);
    if (!scrollContainer) {
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const targetScrollLeft =
      scrollContainer.scrollLeft + (elementRect.left - containerRect.left) - containerRect.width / 2 + elementRect.width / 2;
    const targetScrollTop =
      scrollContainer.scrollTop + (elementRect.top - containerRect.top) - containerRect.height / 2 + elementRect.height / 2;

    scrollContainer.scrollTo({
      left: Math.max(0, targetScrollLeft),
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth'
    });
  }

  private clearBookmarkMarker(): void {
    if (this.bookmarkMarkerElement?.parentElement) {
      this.bookmarkMarkerElement.parentElement.removeChild(this.bookmarkMarkerElement);
    }
    this.bookmarkMarkerElement = null;

    if (this.bookmarkCenterElement?.parentElement) {
      this.bookmarkCenterElement.parentElement.removeChild(this.bookmarkCenterElement);
    }
    this.bookmarkCenterElement = null;

    if (this.bookmarkAnnotationElement) {
      this.bookmarkAnnotationElement.classList.remove('bookmark-annotation-active');
    }
    this.bookmarkAnnotationElement = null;
  }

  private debugLog(event: string, payload: unknown): void {
    if (!this.debugLoggingEnabled) {
      return;
    }

    console.info(`[pdf-viewer] ${event}`, payload);
  }

  private resetViewerState(): void {
    if (this.searchSyncTimer !== null) {
      window.clearTimeout(this.searchSyncTimer);
      this.searchSyncTimer = null;
    }

    if (this.centerHighlightTimer !== null) {
      window.clearTimeout(this.centerHighlightTimer);
      this.centerHighlightTimer = null;
    }

    if (this.bookmarkMarkerTimer !== null) {
      window.clearTimeout(this.bookmarkMarkerTimer);
      this.bookmarkMarkerTimer = null;
    }

    this.clearBookmarkMarker();
    this.activeBookmarkMarker = null;
    this.pdfSourceData = null;// Clear the old bytes out on file swap!

    this.currentPage.set(1);
    this.zoom = 1;
    this.progressPct = 0;
    this.totalPages = 0;
    this.renderedPages = 0;
    this.searchMatches = 0;
    this.lastError = '';
    this.bookmarkTargets.clear();
    this.pdfDocument = null;
    this.loadingState = this.pdfUrl ? 'loading' : 'idle';
  }
}
